import {useState, useCallback, useEffect, useMemo} from 'react';
import produce from 'immer';
import {useSMCSnackbar} from '../../../utils/hooks';
import {useTranslation} from 'react-i18next';
import {setProperty, getErrorCode, isStringI18n, extractI18nString} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes, ErrorCodes} from '../../../api/consts';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import store from '../../../store';
import {push} from 'connected-react-router';
import {SMCPaths} from '../../../conf/Routes';
import {ServerGetSchema} from '../../../api/DBSchema/Server';
import generalIcon from '../../../assets/images/general.png';
import settingsIcon from '../../../assets/images/settings.png';
import modulesIcon from '../../../assets/images/modules.png';
import ServerGeneral from './InnerPages/ServerGeneral';
import ServerSettings from './InnerPages/ServerSettings';
import ServerManagedModules from './InnerPages/ServerManagedModules';
import {helpContextPageUrl} from '../../../conf/Routes';

export function useServerEditor(serverId, defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar, enqueueLogoutSnackbar } = useSMCSnackbar();
    const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

    const [origServerObject, setOrigServerObject] = useState({});
    const [serverObject, setServerObject] = useState({});

    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
    const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

    const [isObjectDirty, setIsObjectDirty] = useState(false);

    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');

    const editorPages = useMemo(() => ([
        { id: ServerGetSchema.pages.general.pageId, title: t('SMP.SYSTEM.SMP_SERVERS.INNER_MENU.GENERAL'), icon: generalIcon, pageElement: ServerGeneral, helpId: helpContextPageUrl.serverGeneral },
        { id: ServerGetSchema.pages.settings.pageId, title: t('SMP.SYSTEM.SMP_SERVERS.INNER_MENU.SETTINGS'), icon: settingsIcon, pageElement: ServerSettings, helpId: helpContextPageUrl.serverSettings },
        { id: ServerGetSchema.pages.managedModules.pageId, title: t('SMP.SYSTEM.SMP_SERVERS.INNER_MENU.MANAGED_MODULES'), icon: modulesIcon, pageElement: ServerManagedModules, helpId: helpContextPageUrl.serverManagedModules }
    ]), [t]);
    
    const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
        const _pageIdToPageName = {};
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToPageName[page.id] = page.title;
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return [_pageIdToPageName, _pageIdToHelpId];
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);

    const fetchServer = useCallback(async () => {
        setIsPageDataLoaded(false);
        try {
            const fetchServerResp = await Api.fetchSingleObject(ObjectTypes.SERVER, serverId);
            const server = fetchServerResp.data.data;
            // Validate server response
            if (!server) {
                console.error('Get server response failed: server response returned empty');
                throw new Error(ErrorCodes.SERVER_ERROR);
            }
            setOrigServerObject(server);
            setServerObject(server);
            setIsPageLoadFailed(false);
            setIsObjectDirty(false); // Just fetched, not dirty
        } catch (e) {
            const errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                enqueueLogoutSnackbar(t(errorCode));
            } else {
                enqueueErrorSnackbar(t(errorCode));
            }
            setIsPageLoadFailed(true);
        }
        setIsPageDataLoaded(true);
    }, [serverId, enqueueErrorSnackbar, enqueueLogoutSnackbar, t]);

    const setServerData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setServerObject(serverObj => produce(serverObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const verifyData = useCallback(() => {
        setIsInnerPageLoading(true);
        Api.verifySingleObject(ObjectTypes.SERVER, serverId, serverObject)
        .then(() => {
            // On no error, clear error message
            setErrorMessagesByPageList(null);
        })
        .catch((error) => {
            const errorMessageArrayByPage = getMessagesArrayByPages(error, pageIdToPageName);
            if (errorMessageArrayByPage) {
                setErrorMessagesByPageList(errorMessageArrayByPage);
            }
        })
        .finally(() => {
            setIsInnerPageLoading(false);
        });
    }, [serverId, getMessagesArrayByPages, serverObject, pageIdToPageName]);

    const onPageChange = useCallback((pageId) => {
        verifyData();
        setChosenPageId(pageId);
    }, [verifyData]);

    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.SERVER, serverId, serverObject)
        .then(() => {
            setIsPageDataLoaded(false);
            setErrorMessagesByPageList(null);
            fetchServer();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch((error) => {
            const errorMessage = getErrorMessage(error);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [fetchServer, getErrorMessage, serverId, serverObject, enqueueSuccessSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (window.confirm(t('OBJECT_EDITOR_OBJ_DELETE', [serverId] ))) {
            Api.deleteObjects(ObjectTypes.SERVER, [serverId])
            .then(res => {
                const deleteResult = res.data.results[0];
                if (deleteResult.success) {
                    setIsObjectDirty(false); // Set as false to not trigger dirty modal
                    store.dispatch(push(SMCPaths.servers));
                    enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_DELETE_OK', [serverId]));
                } else {
                    const deleteErrorCode = deleteResult.errorCode;
                    let deleteErrorParams;
                    if (deleteResult.errorParams) {
                        deleteErrorParams = deleteResult.errorParams.map(param => (
                            isStringI18n(param)) ? t(extractI18nString(param)) : param
                        );
                    }
                    enqueueErrorSnackbar(t(extractI18nString(deleteErrorCode), deleteErrorParams));
                }
            })
            .catch(err => {
                const errorCode = getErrorMessage(err);
                enqueueErrorSnackbar(t(errorCode));
            });
        }
    }, [serverId, getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setServerObject(origServerObject);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
        setErrorMessagesByPageList(null);
    }, [origServerObject]);

    const reloadButtonOnClick = useCallback(() => {
        fetchServer();
        enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_REFRESHED_OK'));
    }, [fetchServer, enqueueSuccessSnackbar, t]);

    // Fetch server on loading of page
    useEffect(() => {
        fetchServer();
    }, [fetchServer]);
    
    return {
        serverObject,
        isObjectDirty,
        editorPages,
        helpId,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setServerData,
        reloadButtonOnClick,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData: fetchServer
    };
}